import { Alert, Box, Collapse, FormGroup, FormParagraph, Heading, Link, List } from '@ksyos/design-system';
import { useState } from 'react';
import type { RespiAllerPrickTestMeasurementsFinalCustomControlData } from '@common/validation/customControls/allergology/respiAllerPrickTestMeasurements/v1/respiAllerPrickTestMeasurementsSchemaV1';
import { ProcessActivityStatus } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { AbstractSyntaxTreeBuilder } from '@common/utils/abstractSyntaxTreeBuilder';
import { savePrickTestMeasurementsExplanationKeys } from '@common/validation/evidencio/evidencio';
import { ProcessItemFooter } from '@frontend/components/pages/episodes/ProcessItemFooter';
import { AbstractSyntaxTreeReactRenderer } from '@frontend/utils/abstractSyntaxTreeReactRenderer';

type RespiAllerPrickTestMeasurementsContentProps = {
    customControlData?: RespiAllerPrickTestMeasurementsFinalCustomControlData;
    status?: ProcessActivityStatus;
};

export function RespiAllerPrickTestMeasurementsContent(props: Readonly<RespiAllerPrickTestMeasurementsContentProps>) {
    const { customControlData, status } = props;
    const [isOpen, setIsOpen] = useState(false);
    const translate = getLanguage();

    return (
        <>
            {status === ProcessActivityStatus.complete && customControlData && (
                <>
                    {renderExplanationItem(savePrickTestMeasurementsExplanationKeys.aanvullendOnderzoek)}
                    {renderExplanationItem(savePrickTestMeasurementsExplanationKeys.positieveUitslagen)}
                    {renderExplanationItem(savePrickTestMeasurementsExplanationKeys.negatieveUitslagen)}

                    <Box overflow="hidden">
                        <Collapse isOpen={isOpen}>
                            <Box marginBottom={6}>
                                <Heading size="xsmall">{translate.allergology.allResults}</Heading>
                                {customControlData.properties.map((property) => (
                                    <FormGroup
                                        key={property.id}
                                        label={property.title}
                                    >
                                        <FormParagraph>{property.value}</FormParagraph>
                                    </FormGroup>
                                ))}
                            </Box>
                        </Collapse>
                    </Box>

                    <ProcessItemFooter
                        isItemOpen={isOpen}
                        labelLess={translate.procedure.hideProcedure}
                        labelMore={translate.procedure.showProcedure}
                        onToggle={async (isOpened) => {
                            setIsOpen(isOpened);
                        }}
                    />
                </>
            )}
            {status !== ProcessActivityStatus.complete && (
                <>
                    <Alert appearance="info">{translate.allergology.waitingForBiometryResults}</Alert>
                    <FormGroup label={translate.allergology.formBiomerty}>
                        <Link
                            download="huidpriktest-formulier.pdf"
                            href="/assets/documents/huidpriktest-formulier.pdf"
                            iconRight="download"
                            variant="externalLink"
                        >
                            {translate.allergology.downloadFormBiometry}
                        </Link>
                    </FormGroup>
                </>
            )}
        </>
    );

    function renderExplanationItem(
        key: (typeof savePrickTestMeasurementsExplanationKeys)[keyof typeof savePrickTestMeasurementsExplanationKeys]
    ) {
        const item = customControlData?.explanation.find((entry) => entry.type === key);

        if (!item) {
            return <></>;
        }

        if (item.type === savePrickTestMeasurementsExplanationKeys.aanvullendOnderzoek) {
            const renderer = new AbstractSyntaxTreeReactRenderer();
            const builder = new AbstractSyntaxTreeBuilder(renderer);

            return builder.render(item.value);
        }

        const title =
            item.type === savePrickTestMeasurementsExplanationKeys.positieveUitslagen
                ? translate.allergology.positiveResults
                : translate.allergology.negativeResults;

        const builder = new AbstractSyntaxTreeBuilder({
            render: (nodes) => {
                return (
                    <>
                        <Box mb={6}>
                            <Heading size="xsmall">{title}</Heading>
                            <List size="small">
                                {nodes.map((node) => (
                                    <List.Item key={node.key}>{node.value}</List.Item>
                                ))}
                            </List>
                        </Box>
                    </>
                );
            },
        });

        return builder.render(item.value);
    }
}
